import RequestStatus from "./RequestStatus";
import CurrencyItem from "./CurrencyItem";
import {ApiError} from "src/model/ApiError";
import IPSServiceOption from "src/model/IPSServiceOption";

export enum FeeType {
    FixedClosingFee = 'FixedClosingFee',
    ReferralFee = 'ReferralFee',
    VariableClosingFee = 'VariableClosingFee',
    FulfillmentFee = 'FulfillmentFee',
    FulfillmentFee_Local = 'FulfillmentFee_Local',
    FulfillmentFee_Regional = 'FulfillmentFee_Regional',
    LowInventoryLevelSurcharge = 'LowInventoryLevelSurcharge',
    BubblewrapFee = 'BubblewrapFee',
    PolybaggingFee = 'PolybaggingFee',
    LabelingFee = 'LabelingFee',
    OpaqueBaggingFee = 'OpaqueBaggingFee',
    TapingFee = 'TapingFee',
    FBAInboundConvenienceFee = 'FBAInboundConvenienceFee',
    DigitalServicesFee = 'DigitalServicesFee',
    ABAFee = 'ABAFee',
    SellerFlexFee= 'SellerFlexFee',
    ImportFees = 'ImportFees'
}

export interface ProgramFees {
    readonly countryCode: string;
    readonly merchantId: string;
    readonly asin: string;
    readonly fnsku: string;
    readonly inventoryUnitInfo?: InventoryUnitInfo;
    readonly programFeeResultMap: Record<string, ProgramFeeResult>; // program name -> program fee
}

export interface InventoryUnitInfo {
    readonly avgInventoryUnitStored?: number;
    readonly estimatedInventoryUnitSales?: number;
    readonly agedInventorySurchargeInfoList?: Array<AgedInventorySurchargeInfo>;
}

export interface AgedInventorySurchargeInfo {
    readonly feeRate: number;
    readonly quantity: number;
    readonly tierName: string;
}

export interface ProgramFeeResult {
    readonly otherCost?: OtherCost;
    readonly perUnitPeakStorageFee?: FeeInfo;
    readonly perUnitNonPeakStorageFee?: FeeInfo;
    readonly otherFeeInfoMap: Record<string, FeeInfo>; // fee type name -> fee
    readonly futureFeeInfoMap?: Record<string, Record<string,FeeInfo>>; // fee type name-> future time -> fee
}

export interface OtherCost {
    readonly vatRate?: number;
    readonly vatAmount?: CurrencyItem;
    readonly ifdRate?: number;
    readonly ifdAmount?: CurrencyItem;
    readonly icmsTaxRate?: number;
    readonly icmsTaxAmount?: CurrencyItem;
    readonly fxRate?: CurrencyItem;
}

export interface FeeInfo {
    readonly feeAmount: CurrencyItem;
    readonly promotionAmount: CurrencyItem;
    readonly taxAmount: CurrencyItem;
    readonly total: CurrencyItem;
    readonly id: string;
    readonly feeNameStringId: string;
    readonly timeOfFeesEstimate: number;
    readonly componentFees: Array<FeeInfo>;
}

export interface ProgramFeesResponse { // used in saga
    readonly succeed: boolean;
    readonly data: ProgramFees;
}

export interface ProgramFeesStore {
    readonly countryCode: string;
    readonly merchantId: string;
    readonly asin: string;
    readonly fnsku: string;
    readonly inventoryUnitInfo?: InventoryUnitInfo;
    readonly programFeeResultWithStatusMap: Record<string, ProgramFeeResultWithStatus>; // program name -> program fee
}

export interface ProgramFeeResultWithStatus {
    readonly taxStatus: RequestStatus;
    readonly requestStatus: RequestStatus;
    readonly programFeeResult: any; // ProgramFeeResult or null;
}

export interface GetProgramFeesResponse { // used in reducer
    readonly requestStatus: RequestStatus;
    readonly programFeesStore: any; // ProgramFeesStore or null
}

export interface GetReportResponse { // used in reducer
    readonly requestStatus: RequestStatus;
    readonly report: any; // Array<Array<string>> or null
    readonly countryCode: string | null;
    readonly error?: ApiError;
}

export interface ProgramParam {
    readonly marketplace: string;
    readonly inventoryPlace?: string;
    readonly cee?: boolean;
    readonly selectedCurrency?: string;
    readonly inventoryMarketplace?: string;
    readonly isFxRateRequired?: boolean;
    readonly inboundingFeeParam?: InboundingFeeParam;
}

export interface InboundingFeeParam {
    readonly regionLocationsMap: Record<string, number>;
    readonly serviceOption: IPSServiceOption;
    readonly unitToInbound: number;
}

export interface ProgramSummary {
    index: number;
    program: string;
    revenue: CurrencyItem;
    cost: CurrencyItem;
    profit: CurrencyItem;
}

export default GetProgramFeesResponse;
